import { useContext, useEffect, useState } from 'react';
import FormsLayout from '../../../layout/FormsLayout';
import { Box, Grid, Link, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MainButton from '../../UI/MainButton';
import RegisterTeacher from './RegisterTeacher';
import RegisterStudent from './RegisterStudent';
import SnackbarContext from '../../../store/snackbar-context';
import { getRegistrationSettings } from '../../../api/content-api';
import authContext from '../../../store/auth-context';
import { loginPage } from '../../../store/fixed-routing';
import { debugError } from '../../../utils/debug';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getStudentRegistrationStatus, getTeacherRegistrationStatus } from '../../../api/auth-api';
import { isFiloOlympicVariant } from '../../../config/variant-profile';

const RegisterForm = () => {
	const isFilo = isFiloOlympicVariant();
	const { palette } = useTheme();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const resumeTeacher = searchParams.get('resume') === 'teacher';
	const resumeStudent = searchParams.get('resume') === 'student';
	const locationStudentPrefill = (location.state as { studentPrefill?: any } | null)?.studentPrefill;
	const [form, setForm] = useState<any>(resumeTeacher ? 'teacher' : resumeStudent ? 'student' : undefined);
	const [teacherPrefill, setTeacherPrefill] = useState<any>(null);
	const [studentPrefill, setStudentPrefill] = useState<any>(locationStudentPrefill ?? null);
	const [registrationSettings, setRegistrationSettings] = useState({
		studentsRegistration: false,
		teachersRegistration: false,
	});
	const { isAdmin, token } = useContext(authContext);
	const { setMsg } = useContext(SnackbarContext);
	const msg = 'Rejestracja jest aktualnie zamknięta';

	useEffect(() => {
		getRegistrationSettings()
			.then((response) => {
				if (response.status === 200) {
					setRegistrationSettings(response.data);
				} else {
					debugError('RegisterForm response error:', response);
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				console.error(exception);
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
			});
	}, [setMsg]);

	useEffect(() => {
		if (resumeTeacher && token) {
			getTeacherRegistrationStatus(token)
				.then((status) => setTeacherPrefill(status?.prefill ?? null))
				.catch((error) => debugError('RegisterForm teacher prefill error:', error));
		}
	}, [resumeTeacher, token]);

	useEffect(() => {
		if (locationStudentPrefill) {
			setStudentPrefill(locationStudentPrefill);
		}
	}, [locationStudentPrefill]);

	useEffect(() => {
		if (resumeStudent && token && !studentPrefill) {
			getStudentRegistrationStatus(token)
				.then((status) => setStudentPrefill(status?.prefill ?? null))
				.catch((error) => debugError('RegisterForm student prefill error:', error));
		}
	}, [resumeStudent, token, studentPrefill]);

	return (
		<FormsLayout>
			{form === undefined && (
				<Grid
					width={'100%'}
					maxWidth={'500px'}
					display={'flex'}
					flexDirection={'column'}
					gap={'48px'}
				>
					<Typography
						variant={'h3'}
						fontWeight={'500'}
						color={palette.primary.main}
						textAlign={'center'}
					>
						Zarejestruj się jako:
					</Typography>
					<Box
						display={'flex'}
						gap={'128px'}
						justifyContent={'center'}
					>
						<Box
							onClick={() => {
								!isAdmin && !registrationSettings.teachersRegistration ? setMsg({ msg }) : setForm('teacher');
							}}
						>
							<MainButton
								id={'register-teacher-button'}
								title={isFilo ? 'Opiekun szkolny/naukowy' : 'Nauczyciel/Opiekun naukowy'}
								disabled={!isAdmin && !registrationSettings.teachersRegistration}
							/>
						</Box>
						<Box
							onClick={() => {
								!isAdmin && !registrationSettings.studentsRegistration ? setMsg({ msg }) : setForm('student');
							}}
						>
							<MainButton
								id={'register-student-button'}
								title={'Uczeń'}
								disabled={!isAdmin && !registrationSettings.studentsRegistration}
							/>
						</Box>
					</Box>
					<Box
						display='flex'
						justifyContent='flex-end'
						width='100%'
					>
						<Link
							component={RouterLink}
							to={loginPage}
							underline='hover'
							sx={{ fontWeight: 500 }}
						>
							Powrót do logowania
						</Link>
					</Box>
					{isAdmin && (!registrationSettings.studentsRegistration || !registrationSettings.teachersRegistration) && (
						<Typography
							color={'red'}
							fontWeight={'bold'}
						>
							Dostęp do rejestracji dla
							{!registrationSettings.studentsRegistration ? ' uczniów ' : ''}
							{!registrationSettings.studentsRegistration && !registrationSettings.teachersRegistration ? '/' : ''}
							{!registrationSettings.teachersRegistration ? ' nauczycieli ' : ''}
							jest zablokowany. Wejście możliwe tylko dla administratorów.
						</Typography>
					)}
				</Grid>
			)}
			{form === 'teacher' ? (
				<RegisterTeacher
					prefill={teacherPrefill}
					resumeMode={resumeTeacher}
				/>
			) : (
				form === 'student' && (
					<RegisterStudent
						prefill={studentPrefill}
						resumeMode={resumeStudent}
					/>
				)
			)}
		</FormsLayout>
	);
};

export default RegisterForm;
